import { SagaIterator } from "redux-saga";
import { call, put, select, spawn, takeEvery } from "redux-saga/effects";

import { ToastContent, getToastContent } from "constants/toasts";
import { navigate } from "helpers/routing";
import { ApiManager } from "services/api";
import {
  CONNECTION_MISSING_ERROR_MESSAGE,
  IConnection,
  IInboxMessage,
  IOutboxMessage,
  InboxMessageTitles,
  POSTMESSAGE_UNAVAILABLE_ERROR_MESSAGE,
  connectionPool,
  createTicketInfoMessage,
} from "services/widget";
import { WidgetIntegrationActionNames } from "store/actions";
import { IApplication, getApplication } from "store/entities/applications";
import { ApplicationsActionNames, ApplicationsActions } from "store/features/applications/actions";
import { ToastAutoHideDelay, ToastVariant, ToastsActions } from "store/features/toasts";
import { IActionWithPayload } from "store/helper";
import { NavigationViewActions } from "store/views/navigation";

const { applicationsApi, clientSsoApi } = ApiManager;

// @ts-expect-error TS(2366)
function sendMessage(connection: IConnection, message?: IOutboxMessage): SagaIterator {
  if (!message) {
    // @ts-expect-error TS(2322)
    return;
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  connection.send(message).catch((e: any) => {
    const isIframeDestroyed = e.code === "DESTROYED";
    const isConnectionUnavailable = [CONNECTION_MISSING_ERROR_MESSAGE, POSTMESSAGE_UNAVAILABLE_ERROR_MESSAGE].includes(
      e.message
    );

    // Ignored issues
    if (isIframeDestroyed || isConnectionUnavailable) {
      return;
    }

    throw e;
  });
}

function* handleOutcomingMessages(action: IActionWithPayload<string, { message: IInboxMessage }>): SagaIterator {
  const connection = connectionPool.get(action.payload.message.pluginId);

  // @ts-expect-error TS(2345)
  sendMessage(connection, action.payload.message);

  // @ts-expect-error TS(2322)
  yield;
}

function* handleIncomingMessages(
  action: IActionWithPayload<string, { message: IInboxMessage; connectionId: string }>
): SagaIterator {
  const connection = connectionPool.getByConnectionId(action.payload.connectionId);
  const { message } = action.payload;

  if (!connection) {
    return;
  }

  if (message.title === InboxMessageTitles.SetFullscreenWidgetNotificationBadge) {
    yield put(
      NavigationViewActions.setNavigationItemBadge({
        itemId: message.pluginId,
        badge: message.data,
      })
    );
  }

  if (message.title === InboxMessageTitles.PluginInited) {
    setTimeout((): void => {
      const { ticketInfo } = connection.getContext();
      const ticketInfoMessage = createTicketInfoMessage(message.pluginId, ticketInfo);
      sendMessage(connection, ticketInfoMessage);
    }, 100);
  }
}

function* installApplication(action: IActionWithPayload<string, { id: string }>): SagaIterator {
  const { error } = yield call(applicationsApi.install, action.payload.id);
  const application: IApplication = yield select(getApplication, action.payload.id);

  if (error) {
    yield put(ApplicationsActions.installApplicationFailure(error));
  } else {
    yield put(ApplicationsActions.installApplicationSuccessful(application));
  }
}

function* installationFailure(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.APPLICATION_INSTALL_ERROR),
      autoHideDelayTime: ToastAutoHideDelay.Long,
      variant: ToastVariant.Error,
    })
  );
}

export function* uninstallApplication(
  action: IActionWithPayload<string, { id: string; redirectTo?: string; reason?: string }>
): SagaIterator {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line no-unsafe-optional-chaining
  const { reason, redirectTo, id } = action?.payload;
  const { error } = yield call(applicationsApi.uninstall, id, { reason });

  if (error) {
    yield put(ApplicationsActions.uninstallApplicationFailure(error));
  } else {
    yield put(ApplicationsActions.uninstallApplicationSuccessful(id));

    if (redirectTo) {
      yield call(navigate, redirectTo);
    }
  }
}

function* uninstallationSuccessful(action: IActionWithPayload<string, { id: string }>): SagaIterator {
  const deletedApplication: IApplication = yield select(getApplication, action?.payload?.id);
  const deletedAppClientId = deletedApplication?.clientId;

  if (deletedAppClientId) {
    // Delete user's consents from deleted application
    yield spawn(clientSsoApi.deleteConsent, deletedAppClientId);
  }

  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.APPLICATION_UNINSTALL_SUCCESS),
      autoHideDelayTime: ToastAutoHideDelay.Long,
      variant: ToastVariant.Success,
    })
  );
}

function* uninstallationFailure(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.APPLICATION_UNINSTALL_ERROR),
      autoHideDelayTime: ToastAutoHideDelay.Long,
      variant: ToastVariant.Error,
    })
  );
}

export function* applicationsSagas(): SagaIterator {
  yield takeEvery(WidgetIntegrationActionNames.WIDGET_INBOX_MESSAGE, handleIncomingMessages);
  yield takeEvery(WidgetIntegrationActionNames.WIDGET_OUTBOX_MESSAGE, handleOutcomingMessages);
  yield takeEvery(ApplicationsActionNames.INSTALL_APPLICATION, installApplication);
  yield takeEvery(ApplicationsActionNames.INSTALL_APPLICATION_FAILURE, installationFailure);
  yield takeEvery(ApplicationsActionNames.UNINSTALL_APPLICATION, uninstallApplication);
  yield takeEvery(ApplicationsActionNames.UNINSTALL_APPLICATION_SUCCESSFUL, uninstallationSuccessful);
  yield takeEvery(ApplicationsActionNames.UNINSTALL_APPLICATION_FAILURE, uninstallationFailure);
}
